import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { AcademyCourseAssignment } from '../components/academy/admin/AcademyCourseAssignment';
import { AcademyParticipantDetails } from '../components/academy/admin/AcademyParticipantDetails';
import { AcademyParticipantForm } from '../components/academy/admin/AcademyParticipantForm';
import { AcademyParticipantTable } from '../components/academy/admin/AcademyParticipantTable';
import { PageHeader } from '../components/ui/PageHeader';
import { useUserRole, ROLES } from '../context/UserRoleContext';
import { getAcademyParticipants } from '../services/academyParticipantReadService';

const { FiRefreshCw, FiSearch } = FiIcons;

function sortParticipants(rows, sort) {
  return [...rows].sort((a, b) => {
    if (sort === 'activity') {
      return new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0);
    }
    return a.name.localeCompare(b.name, 'nb');
  });
}

export function AcademyParticipantRegister() {
  const { role, appUser } = useUserRole();
  const [data, setData] = useState({ participants: [], organizations: [] });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [organization, setOrganization] = useState('');
  const [participantStatus, setParticipantStatus] = useState('');
  const [sort, setSort] = useState('name');
  const [selected, setSelected] = useState(null);
  const [showParticipantForm, setShowParticipantForm] = useState(false);
  const [showAssignment, setShowAssignment] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const next = await getAcademyParticipants();
      setData(next);
      setStatus('ready');
      return next;
    } catch (failure) {
      setError(failure?.message || 'Kursdeltakerregisteret kunne ikke lastes.');
      setStatus('error');
      throw failure;
    }
  }, []);

  useEffect(() => {
    void load().catch(() => {});
  }, [load]);

  const participants = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('nb');
    const filtered = data.participants.filter((participant) => {
      const matchesSearch = !query ||
        participant.name.toLocaleLowerCase('nb').includes(query) ||
        participant.email.toLocaleLowerCase('nb').includes(query);
      const matchesOrganization = role !== ROLES.SUPERADMIN ||
        !organization || participant.organizationId === organization;
      const matchesStatus = !participantStatus || participant.status === participantStatus;
      return matchesSearch && matchesOrganization && matchesStatus;
    });
    return sortParticipants(filtered, sort);
  }, [data.participants, organization, participantStatus, role, search, sort]);

  const refreshAfterCreate = async () => {
    const next = await load();
    setSuccess('Deltakeren er registrert.');
    return next;
  };

  const refreshAfterAssignment = async () => {
    const participantId = selected?.id;
    const next = await load();
    setSelected(next.participants.find((item) => item.id === participantId) || null);
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title="Kursdeltakere"
        intro="Finn deltakere og se påmeldinger, fremdrift, testresultater og kursbevis."
        breadcrumb="HMS-innsikt / Academy Admin"
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setSuccess('');
                setShowParticipantForm(true);
              }}
              className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800"
            >
              Ny kursdeltaker
            </button>
            <button
              type="button"
              onClick={() => void load().catch(() => {})}
              disabled={status === 'loading'}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-wait disabled:opacity-60"
            >
              <SafeIcon icon={FiRefreshCw} className={status === 'loading' ? 'animate-spin' : ''} />
              Oppdater
            </button>
          </div>
        }
      />

      {success && <p role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">{success}</p>}

      <section aria-label="Søk og filtrer deltakere" className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-4">
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-slate-700">Søk etter navn eller e-post</span>
          <span className="relative block">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Søk etter deltaker"
              className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm"
            />
          </span>
        </label>

        {role === ROLES.SUPERADMIN && (
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-slate-700">Organisasjon</span>
            <select
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            >
              <option value="">Alle organisasjoner</option>
              {data.organizations.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-slate-700">Deltakerstatus</span>
          <select
            value={participantStatus}
            onChange={(event) => setParticipantStatus(event.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          >
            <option value="">Alle statuser</option>
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
            <option value="archived">Arkivert</option>
          </select>
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-slate-700">Sorter etter</span>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          >
            <option value="name">Navn</option>
            <option value="activity">Siste aktivitet</option>
          </select>
        </label>
      </section>

      {status === 'loading' && <p role="status" className="rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-600">Laster deltakere fra Academy …</p>}
      {status === 'error' && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-5">
          <h2 className="font-semibold text-rose-800">Deltakerregisteret kunne ikke lastes</h2>
          <p className="mt-2 text-sm text-rose-700">{error}</p>
        </div>
      )}
      {status === 'ready' && <AcademyParticipantTable participants={participants} onSelect={setSelected} />}
      <AcademyParticipantDetails
        participant={selected}
        onClose={() => setSelected(null)}
        onAssign={() => setShowAssignment(true)}
      />

      {showParticipantForm && (
        <AcademyParticipantForm
          organizations={data.organizations}
          role={role}
          organizationId={appUser?.organization_id}
          onClose={() => setShowParticipantForm(false)}
          onCreated={refreshAfterCreate}
        />
      )}
      {showAssignment && selected && (
        <AcademyCourseAssignment
          participant={selected}
          onClose={() => setShowAssignment(false)}
          onAssigned={refreshAfterAssignment}
        />
      )}
    </div>
  );
}

export default AcademyParticipantRegister;